/**
 * BYOR source adapters — paste and URL modes (§4.8.1).
 *
 * Modes 2 and 3 (pasted HTML, pasted plain text) run **entirely in the
 * browser**. The content never leaves the device, cannot be blocked by a bot
 * wall or a paywall, and works with both Workers deleted. §4.7 is explicit that
 * on a hosted tool these are not a fallback — they are the safest and most
 * reliable path, and they must work first and work best.
 *
 * Mode 1 (URL) is a convenience layer on top, routed through `/api/fetch`. It
 * is the project's single riskiest component and is gated accordingly: it can
 * be switched off with one environment variable (FR-71), and when it is off
 * this module reports that cleanly so the UI can hide URL mode and point people
 * at paste mode instead.
 */

import type { PlayerUniverse, Position, RankingRow, RankingSource, ScoringFormat, UnmatchedRow } from '../core/types.ts';
import { PlayerIndex } from '../core/matching.ts';
import {
  buildPreview,
  extractRankings,
  type CandidateBlock,
  type ExtractionPreview,
  type ExtractionResult,
  type ExtractOptions,
} from '../core/byor/extract.ts';
import { checkReplay, resolveDiscoveryUrl, type Recipe, type ReplayCheck } from '../core/byor/recipes.ts';
import { fetchWithRetry, HttpError } from './http.ts';

export interface PasteImportInput {
  /** Raw pasted content — HTML or plain text; the extractor decides which. */
  content: string;
  source_name: string;
  /** Optional attribution link the user supplies for their own reference (R3). */
  source_url?: string | null;
  scoring_format?: ScoringFormat;
  threshold?: number;
  matchThreshold?: number;
}

export interface ImportOutcome {
  result: ExtractionResult;
  preview: ExtractionPreview | null;
  /** Null until the user confirms the preview (FR-48). */
  source: RankingSource | null;
}

/**
 * Run the extraction pipeline over pasted content and produce a preview.
 *
 * Note what this does *not* do: it never returns a usable `RankingSource`.
 * Committing requires an explicit `commitImport` call after the user has seen
 * the preview, which is how FR-48's "mandatory and non-skippable" requirement
 * is enforced by the type system rather than by UI discipline.
 */
export function importFromPaste(input: PasteImportInput, index: PlayerIndex): ImportOutcome {
  const options: ExtractOptions = {
    threshold: input.threshold ?? 0.7,
    matchThreshold: input.matchThreshold ?? 0.86,
  };
  const result = extractRankings(input.content, index, options);
  return {
    result,
    preview: result.best ? buildPreview(result.best, options.threshold!) : null,
    source: null,
  };
}

export interface CommitOptions {
  source_id: string;
  source_name: string;
  source_url: string | null;
  scoring_format?: ScoringFormat;
  tier?: 1 | 2 | 3;
  as_of_date?: string;
  /** Rows the user chose to drop in the preview, by row index. */
  dropped?: Set<number>;
  /** Manual name→player resolutions made in the preview (FR-49). */
  overrides?: Map<number, string>;
  /**
   * Resolves a matched player's position from the universe.
   *
   * Needed because most pasted lists carry no position column at all — a bare
   * "1. Jahmyr Gibbs DET" has nothing to infer from. Without this the committed
   * source would have no positional ranks, and every downstream feature keyed
   * on position (tiers, scarcity, the curve projections) would treat a
   * user-supplied source as second-class, which FR-53 forbids.
   */
  positionOf?: (playerKey: string) => Position | null;
}

/**
 * Turn a confirmed extraction into a first-class ranking source (FR-53).
 *
 * "First-class" is load-bearing: the result is an ordinary `RankingSource`, so
 * it can be the active board sort, join the blend with its own weight, and
 * appear in per-player comparison — with no branch anywhere downstream that
 * knows it came from a paste.
 */
export function commitImport(block: CandidateBlock, opts: CommitOptions): RankingSource {
  const rows: RankingRow[] = [];
  const unmatched: UnmatchedRow[] = [];
  let rank = 0;

  block.rows.forEach((row, i) => {
    if (opts.dropped?.has(i)) return;
    const override = opts.overrides?.get(i);
    const player_key = override ?? row.player_key;

    if (!player_key) {
      // FR-19: never silently drop a ranked player. Unmatched rows are carried
      // on the source itself so the UI can keep offering to resolve them.
      unmatched.push({
        raw_name: row.name,
        overall_rank: row.rank,
        team: row.team,
        position: row.position,
        suggestions: row.suggestions,
      });
      return;
    }

    rank++;
    rows.push({
      source_id: opts.source_id,
      player_key,
      raw_name: row.name,
      overall_rank: rank,
      position_rank: null,
      tier: row.tier,
      team: row.team,
      position: row.position ?? opts.positionOf?.(player_key) ?? null,
      notes: row.notes,
    });
  });

  return {
    source_id: opts.source_id,
    source_name: opts.source_name,
    source_url: opts.source_url,
    as_of_date: opts.as_of_date ?? new Date().toISOString().slice(0, 10),
    scoring_format: opts.scoring_format ?? 'unknown',
    tier: opts.tier ?? 3,
    kind: 'user',
    rows: withPositionRanks(rows),
    unmatched,
  };
}

function withPositionRanks(rows: RankingRow[]): RankingRow[] {
  const counters = new Map<string, number>();
  return rows.map((row) => {
    if (!row.position) return row;
    const next = (counters.get(row.position) ?? 0) + 1;
    counters.set(row.position, next);
    return { ...row, position_rank: next };
  });
}

// ---------------------------------------------------------------------------
// URL mode (§4.8.1 mode 1) — routed through /api/fetch
// ---------------------------------------------------------------------------

export class UrlFetchDisabledError extends Error {
  constructor() {
    super('URL fetching is disabled on this deployment. Paste the list instead — it always works.');
    this.name = 'UrlFetchDisabledError';
  }
}

export class UrlFetchBlockedError extends Error {
  readonly reason: string;
  constructor(reason: string) {
    super(reason);
    this.name = 'UrlFetchBlockedError';
    this.reason = reason;
  }
}

export interface UrlFetchOptions {
  url: string;
  /** Turnstile token, when the deployment requires one (FR-74). */
  turnstileToken?: string | null;
  signal?: AbortSignal;
  endpoint?: string;
}

/**
 * Fetch a page through the Worker conduit.
 *
 * Every error path here exists to funnel the user toward paste mode rather than
 * leaving them stuck. Expect this to fail on large media properties regardless
 * of correctness: Cloudflare Worker egress IPs are widely bot-blocked, so a
 * server-side fetch of a Yahoo article will plausibly return a challenge page
 * while the same fetch from the user's own browser session succeeds (§4.7).
 * That is a product fact, not a bug, and the UI must say so plainly.
 */
export async function fetchUrlContent(opts: UrlFetchOptions): Promise<string> {
  const endpoint = opts.endpoint ?? '/api/fetch';
  const url = `${endpoint}?url=${encodeURIComponent(opts.url)}`;

  let res: Response;
  try {
    res = await fetchWithRetry(url, {
      signal: opts.signal,
      timeoutMs: 20_000,
      // One inbound request, at most one outbound fetch (FR-67). Retrying here
      // would multiply requests at the third party, so it is disabled.
      retries: 0,
      headers: opts.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : {},
    });
  } catch (err) {
    if (err instanceof HttpError) {
      if (err.status === 404 || err.status === 501) throw new UrlFetchDisabledError();
      if (err.status === 403) {
        throw new UrlFetchBlockedError(
          'The site refused the request, or its robots.txt disallows it. Open the page yourself and paste it instead.',
        );
      }
      if (err.status === 429) {
        throw new UrlFetchBlockedError('Rate limit reached. Wait a minute, or use paste mode.');
      }
      if (err.status === 400) {
        throw new UrlFetchBlockedError('That URL was rejected as unsafe or unsupported.');
      }
    }
    throw err;
  }

  return res.text();
}

export interface RecipeReplayOutcome {
  check: ReplayCheck;
  result: ExtractionResult;
  preview: ExtractionPreview | null;
  /** Only populated when the guard passed — otherwise the caller keeps old data. */
  source: RankingSource | null;
  resolved_url: string | null;
}

/**
 * Replay a saved recipe with no user interaction (FR-51).
 *
 * The guard is the whole point. If confidence drops more than 15 points or the
 * row count moves more than 40%, this returns `needs-reconfirm` with the old
 * data untouched, rather than quietly writing a corrupted list to the board
 * mid-draft.
 */
export async function replayRecipe(
  recipe: Recipe,
  index: PlayerIndex,
  opts: { signal?: AbortSignal; endpoint?: string; turnstileToken?: string | null } = {},
): Promise<RecipeReplayOutcome> {
  let target = recipe.url;

  // §4.2: URL discovery must not be hardcoded to a single slug, because sources
  // repost and update under new URLs.
  if (recipe.discovery) {
    const indexHtml = await fetchUrlContent({
      url: recipe.discovery.index_url,
      signal: opts.signal,
      endpoint: opts.endpoint,
      turnstileToken: opts.turnstileToken,
    });
    target = resolveDiscoveryUrl(indexHtml, recipe.discovery) ?? recipe.url;
  }

  if (!target) {
    return {
      check: {
        verdict: 'failed',
        reasons: ['Recipe has no URL and discovery found no matching article.'],
        confidence_delta: 0,
        row_delta: 0,
      },
      result: { best: null, candidates: [], rejected: true, threshold: 0.7 },
      preview: null,
      source: null,
      resolved_url: null,
    };
  }

  const content = await fetchUrlContent({
    url: target,
    signal: opts.signal,
    endpoint: opts.endpoint,
    turnstileToken: opts.turnstileToken,
  });

  const result = extractRankings(content, index, {
    strategy: recipe.strategy,
    strategyIndex: recipe.strategy_index,
    columns: recipe.columns,
  });

  const check = checkReplay(recipe, result);
  const preview = result.best ? buildPreview(result.best) : null;

  return {
    check,
    result,
    preview,
    source:
      check.verdict === 'ok' && result.best
        ? commitImport(result.best, {
            source_id: recipe.slug,
            source_name: recipe.source_name,
            source_url: target,
            scoring_format: recipe.scoring_format,
          })
        : null,
    resolved_url: target,
  };
}

/** Drop rows for players not in the current universe, keeping ranks dense. */
export function reconcile(source: RankingSource, universe: PlayerUniverse): RankingSource {
  const kept = source.rows.filter((r) => r.player_key && universe.has(r.player_key));
  return { ...source, rows: kept.map((r, i) => ({ ...r, overall_rank: i + 1 })) };
}
